import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../services/tokenService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Pencil, CheckCircle2, XCircle, Upload, Star, Eye, EyeOff,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  ['strategique', 'Stratégique'], ['territorial', 'Territorial'], ['filiere', 'Filière'],
  ['projet', 'Projet'], ['competences', 'Compétences'], ['nature', 'Nature'],
  ['media', 'Média'], ['institutionnel', 'Institutionnel'],
];
const _STATUTS = [
  'candidature_recue', 'en_qualification', 'validee', 'convention_preparation',
  'convention_signee', 'facturee', 'actif', 'en_renouvellement', 'expire', 'suspendu', 'refuse',
];

const EMPTY = {
  name: '', legal_name: '', siren: '', sponsor_category: 'territorial', website_url: '',
  secteur_activite: '', short_description: '', full_description: '',
  territories: '', branches: '', contribution_types: '', start_date: '',
};

export default function AdminSponsorsPage() {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sponsors');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${getToken()}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/api/admin/sponsors`, auth),
        axios.get(`${API}/api/admin/sponsors/candidatures`, auth),
      ]);
      setSponsors(s.data.sponsors || []);
      setCandidatures(c.data.candidatures || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (sp) => {
    setEditing(sp);
    setForm({
      ...EMPTY, ...sp,
      territories: (sp.territories || []).join(', '),
      branches: (sp.branches || []).join(', '),
      contribution_types: (sp.contribution_types || []).join(', '),
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    const payload = {
      ...form,
      territories: form.territories ? form.territories.split(',').map((x) => x.trim()).filter(Boolean) : [],
      branches: form.branches ? form.branches.split(',').map((x) => x.trim()).filter(Boolean) : [],
      contribution_types: form.contribution_types ? form.contribution_types.split(',').map((x) => x.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/sponsors/${editing.id}`, payload, auth);
        toast.success('Sponsor mis à jour');
      } else {
        await axios.post(`${API}/api/admin/sponsors`, payload, auth);
        toast.success('Sponsor créé');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const setStatut = async (sp, patch) => {
    try {
      await axios.patch(`${API}/api/admin/sponsors/${sp.id}/statut`, patch, auth);
      load();
    } catch { toast.error('Erreur mise à jour statut'); }
  };

  const remove = async (sp) => {
    if (!window.confirm(`Supprimer « ${sp.name} » ?`)) return;
    try { await axios.delete(`${API}/api/admin/sponsors/${sp.id}`, auth); toast.success('Supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
  };

  const uploadLogo = async (sp, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/admin/sponsors/${sp.id}/logo`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo mis à jour');
      load();
    } catch { toast.error('Erreur upload logo'); }
  };

  const treatCandidature = async (c, status) => {
    try {
      await axios.patch(`${API}/api/admin/sponsors/candidatures/${c.id}`, { status }, auth);
      toast.success('Candidature mise à jour');
      load();
    } catch { toast.error('Erreur'); }
  };

  const field = (k, label, type = 'text') => (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-300">{label}</Label>
      <Input
        type={type} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="bg-slate-800 border-slate-700 text-white" data-testid={`sponsor-field-${k}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="admin-sponsors-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn"
              className="border-slate-700 text-slate-300"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
            <h1 className="text-2xl font-bold">Partenaires &amp; Sponsors</h1>
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-sponsor-btn">
            <Plus className="w-4 h-4 mr-1" />Nouveau sponsor
          </Button>
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('sponsors')} data-testid="tab-sponsors"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'sponsors' ? 'bg-emerald-600' : 'bg-slate-800 text-slate-400'}`}>
            Sponsors ({sponsors.length})
          </button>
          <button onClick={() => setTab('candidatures')} data-testid="tab-candidatures"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'candidatures' ? 'bg-emerald-600' : 'bg-slate-800 text-slate-400'}`}>
            Candidatures ({candidatures.length})
          </button>
        </div>

        {loading && <p className="text-slate-400">Chargement…</p>}

        {!loading && tab === 'sponsors' && (
          <div className="space-y-3" data-testid="sponsors-admin-list">
            {sponsors.length === 0 && <p className="text-slate-500">Aucun sponsor pour l'instant.</p>}
            {sponsors.map((sp) => (
              <div key={sp.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4"
                data-testid={`admin-sponsor-row-${sp.slug}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {sp.logo_url
                    ? <img src={sp.logo_url.startsWith('http') ? sp.logo_url : `${API}${sp.logo_url}`} alt={sp.name} className="w-12 h-12 rounded-lg object-contain bg-white/5" />
                    : <div className="w-12 h-12 rounded-lg bg-slate-800" />}
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {sp.name}
                      {sp.featured && <Star className="w-4 h-4 text-amber-400" />}
                    </div>
                    <div className="text-xs text-slate-400">{sp.sponsor_category} · {(sp.territories || []).join(', ')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={sp.status === 'actif' ? 'bg-emerald-600' : 'bg-slate-700'}>{sp.status}</Badge>
                  <Badge className={sp.publie ? 'bg-blue-600' : 'bg-slate-700'}>{sp.publie ? 'publié' : 'brouillon'}</Badge>
                  <label className="cursor-pointer text-slate-400 hover:text-white" title="Logo" data-testid={`logo-upload-${sp.slug}`}>
                    <Upload className="w-4 h-4" />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(sp, e.target.files[0])} />
                  </label>
                  <button onClick={() => setStatut(sp, { publie: !sp.publie, status: sp.publie ? sp.status : 'actif' })}
                    className="text-slate-400 hover:text-white" title="Publier/Dépublier" data-testid={`toggle-publish-${sp.slug}`}>
                    {sp.publie ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setStatut(sp, { featured: !sp.featured })} className="text-slate-400 hover:text-amber-400" title="Mettre en avant" data-testid={`toggle-featured-${sp.slug}`}>
                    <Star className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(sp)} className="text-slate-400 hover:text-white" title="Modifier" data-testid={`edit-${sp.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(sp)} className="text-slate-400 hover:text-red-400" title="Supprimer" data-testid={`delete-${sp.slug}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'candidatures' && (
          <div className="space-y-3" data-testid="candidatures-list">
            {candidatures.length === 0 && <p className="text-slate-500">Aucune candidature.</p>}
            {candidatures.map((c) => (
              <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between gap-4"
                data-testid={`candidature-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.organisation_name}</div>
                  <div className="text-xs text-slate-400">{c.contact_name} · {c.email} · {c.sponsor_category || '—'} · {c.territory || '—'}</div>
                  {c.message && <div className="text-xs text-slate-500 mt-1 truncate max-w-xl">{c.message}</div>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className="bg-slate-700 text-white font-semibold">{c.status}</Badge>
                  <button onClick={() => treatCandidature(c, 'validee')} className="text-emerald-400 hover:text-emerald-300" title="Valider" data-testid={`validate-cand-${c.id}`}><CheckCircle2 className="w-5 h-5" /></button>
                  <button onClick={() => treatCandidature(c, 'refuse')} className="text-red-400 hover:text-red-300" title="Refuser" data-testid={`reject-cand-${c.id}`}><XCircle className="w-5 h-5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le sponsor' : 'Nouveau sponsor'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('name', 'Nom *')}
            {field('legal_name', 'Raison sociale')}
            {field('siren', 'SIREN')}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-300">Catégorie</Label>
              <select value={form.sponsor_category} onChange={(e) => setForm({ ...form, sponsor_category: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md h-10 px-3 text-white text-sm" data-testid="sponsor-field-category">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {field('website_url', 'Site internet')}
            {field('secteur_activite', "Secteur d'activité")}
            {field('territories', 'Territoires (virgules)')}
            {field('branches', 'Branches (virgules)')}
            {field('contribution_types', 'Types de contribution (virgules)')}
            {field('start_date', 'Partenaire depuis (AAAA-MM-JJ)')}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-300">Présentation courte</Label>
            <Textarea value={form.short_description || ''} onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" data-testid="sponsor-field-short" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-300">Présentation détaillée</Label>
            <Textarea value={form.full_description || ''} onChange={(e) => setForm({ ...form, full_description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" rows={4} data-testid="sponsor-field-full" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-sponsor-btn">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
